// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useState } from 'react';
import { useModuleParty } from '../../../contexts/ModuleContext';
import { useGetCollateralAgreement } from '../common/useGetCollateralAgreement';
import { GenericAction } from '../types';
import { CollateralStateRow, UseCollateralActionsDialog } from './types';

export const useCollateralActions = () => {
  const party = useModuleParty();
  const { getCollateralAgreement } = useGetCollateralAgreement();

  const [dialogName, setDialogName] = useState<UseCollateralActionsDialog>(null);
  const [onTerminate, setOnTerminate] = useState<(() => Promise<unknown>) | undefined>();

  const closeDialog = useCallback(() => {
    setDialogName(null);
  }, [setDialogName]);

  const createCollateralActions = useCallback(
    (r: CollateralStateRow) => {
      const collateralAgreement = getCollateralAgreement(r.payload);

      const actions: GenericAction<CollateralStateRow>[] = [];

      const isPartyCalculationAgent =
        collateralAgreement && collateralAgreement.payload.calculationAgent === party;

      if (isPartyCalculationAgent) {
        actions.push({
          inMoreMenu: true,
          name: 'Call initial margin',
          action: () => {
            setDialogName('callInitialMargin');
            setOnTerminate(() => () => new Promise(() => {}));
            return Promise.resolve();
          },
          buttonProps: { variant: 'outlined' },
        });

        actions.push({
          inMoreMenu: true,
          name: 'Call variation margin',
          action: () => {
            setDialogName('callVariationMargin');
            setOnTerminate(() => () => new Promise(() => {}));
            return Promise.resolve();
          },
          buttonProps: { variant: 'outlined' },
        });

        actions.push({
          inMoreMenu: true,
          name: 'Call independent margin',
          action: () => {
            setDialogName('callIndependentMargin');
            setOnTerminate(() => () => new Promise(() => {}));
            return Promise.resolve();
          },
          buttonProps: { variant: 'outlined' },
        });
      }

      return actions;
    },
    [getCollateralAgreement, party],
  );

  return {
    dialogName,
    onTerminate,
    closeDialog,
    createCollateralActions,
  };
};
